import { useForm, Link } from "@inertiajs/react";
import type Producer from "@/types/serializers/Producer";
import type Country from "@/types/serializers/Country";
import { useFlashToast } from "@/hooks/useFlashToast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CountryCombobox } from "@/components/CountryCombobox";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";

type Props = {
  producer: Producer;
  countries: Country[];
  errors?: Record<string, string[]>;
  flash?: { alert?: string };
};

export default function ProducersNew({ producer, countries, errors = {}, flash }: Props) {
  useFlashToast(flash);

  const form = useForm({
    producer: {
      name: producer.name ?? "",
      description: producer.description ?? "",
      address_line1: producer.addressLine1 ?? "",
      address_line2: producer.addressLine2 ?? "",
      city: producer.city ?? "",
      state: producer.state ?? "",
      postal_code: producer.postalCode ?? "",
      country_id: producer.country?.id ? String(producer.country.id) : "",
    },
  });

  const p = form.data.producer;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    form.post("/producers");
  }

  return (
    <div className="py-8 px-4">
      <div className="mx-auto max-w-xl space-y-6">
        <Button variant="ghost" size="sm" asChild>
          <Link href="/producers">
            <ArrowLeft className="size-4" />
            Back to producers
          </Link>
        </Button>

        <Card>
          <CardHeader>
            <CardTitle>New Producer</CardTitle>
            <CardDescription>
              Add a new producer to your collection.
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="e.g. Buffalo Trace, Diageo…"
                  value={p.name}
                  onChange={(e) =>
                    form.setData("producer", { ...p, name: e.target.value })
                  }
                  aria-invalid={!!errors.name}
                  autoFocus
                  required
                />
                {errors.name?.[0] && (
                  <p className="text-sm text-destructive">{errors.name[0]}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Optional description for this producer…"
                  value={p.description}
                  onChange={(e) =>
                    form.setData("producer", { ...p, description: e.target.value })
                  }
                  rows={3}
                  aria-invalid={!!errors.description}
                />
                {errors.description?.[0] && (
                  <p className="text-sm text-destructive">{errors.description[0]}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="address_line1">Address line 1</Label>
                <Input
                  id="address_line1"
                  type="text"
                  value={p.address_line1}
                  onChange={(e) =>
                    form.setData("producer", { ...p, address_line1: e.target.value })
                  }
                  aria-invalid={!!errors.address_line1}
                />
                {errors.address_line1?.[0] && (
                  <p className="text-sm text-destructive">{errors.address_line1[0]}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="address_line2">Address line 2</Label>
                <Input
                  id="address_line2"
                  type="text"
                  value={p.address_line2}
                  onChange={(e) =>
                    form.setData("producer", { ...p, address_line2: e.target.value })
                  }
                  aria-invalid={!!errors.address_line2}
                />
                {errors.address_line2?.[0] && (
                  <p className="text-sm text-destructive">{errors.address_line2[0]}</p>
                )}
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    type="text"
                    value={p.city}
                    onChange={(e) =>
                      form.setData("producer", { ...p, city: e.target.value })
                    }
                    aria-invalid={!!errors.city}
                  />
                  {errors.city?.[0] && (
                    <p className="text-sm text-destructive">{errors.city[0]}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    type="text"
                    value={p.state}
                    onChange={(e) =>
                      form.setData("producer", { ...p, state: e.target.value })
                    }
                    aria-invalid={!!errors.state}
                  />
                  {errors.state?.[0] && (
                    <p className="text-sm text-destructive">{errors.state[0]}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="postal_code">Postal code</Label>
                  <Input
                    id="postal_code"
                    type="text"
                    value={p.postal_code}
                    onChange={(e) =>
                      form.setData("producer", { ...p, postal_code: e.target.value })
                    }
                    aria-invalid={!!errors.postal_code}
                  />
                  {errors.postal_code?.[0] && (
                    <p className="text-sm text-destructive">{errors.postal_code[0]}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="country_id">Country</Label>
                <CountryCombobox
                  id="country_id"
                  countries={countries}
                  value={p.country_id}
                  onValueChange={(value) =>
                    form.setData("producer", { ...p, country_id: value })
                  }
                  aria-invalid={!!errors.country}
                />
                {errors.country?.[0] && (
                  <p className="text-sm text-destructive">{errors.country[0]}</p>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline" asChild>
                <Link href="/producers">Cancel</Link>
              </Button>
              <Button type="submit" disabled={form.processing}>
                {form.processing ? "Creating…" : "Create Producer"}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}
